import type { FeedFormat, FeedSort, FeedView, Platform } from '../api/types'

// Вкладки ленты. Видео — рилсы Instagram (позже YouTube Shorts и TikTok),
// тексты — Telegram, Reddit, карусели и картинки Instagram. У видео два вида:
// «Авто» (собранные) и «Добавленные» (команда добавила по ссылке).

export const FEED_PERIODS = [
  { days: 1, label: 'За сутки' },
  { days: 3, label: 'За 3 дня' },
  { days: 7, label: 'За 7 дней' },
  { days: 30, label: 'За 30 дней' },
  { days: 90, label: 'За 90 дней' },
]

export const FORMATS: FeedFormat[] = ['video', 'text']

export const FORMAT_LABEL: Record<FeedFormat, string> = { video: 'Видео', text: 'Тексты' }

export const FORMAT_PLATFORMS: Record<FeedFormat, Platform[]> = {
  video: ['instagram'],
  text: ['telegram', 'reddit', 'instagram'],
}

export function isFormat(value: string | null): value is FeedFormat {
  return value === 'video' || value === 'text'
}

// Сортировки, у которых есть величина. Та же таблица, что SORTS_BY_SLICE
// в backend/src/cf2/feed/query.py: просмотров нет у каруселей, картинок и
// Reddit, пересылки есть только у Telegram. У «Добавленных» свой список:
// ×N есть не у всех, вместо него доля просмотров от подписчиков.
const ALL_SORTS: FeedSort[] = ['xn', 'new', 'views', 'likes', 'comments', 'forwards']
const ADDED_SORTS: FeedSort[] = ['added', 'share', 'views', 'likes', 'comments', 'new']

export function sortsFor(format: FeedFormat, platform: Platform | undefined, view: FeedView = 'auto'): FeedSort[] {
  if (format === 'video') return view === 'added' ? ADDED_SORTS : ['xn', 'new', 'views', 'likes', 'comments']
  if (!platform || platform === 'telegram') return ALL_SORTS
  return ['xn', 'new', 'likes', 'comments']
}

export function defaultSort(view: FeedView): FeedSort {
  return view === 'added' ? 'added' : 'xn'
}

export function sortLabel(sort: FeedSort, format: FeedFormat, platform: Platform | undefined, view: FeedView = 'auto'): string {
  switch (sort) {
    case 'xn':
      return 'По ×N'
    case 'new':
      // Рядом с «Сначала добавленные» просто «новые» читается двояко.
      return view === 'added' ? 'Сначала новые публикации' : 'Сначала новые'
    case 'views':
      return 'Больше просмотров'
    case 'likes':
      if (format === 'video' || platform === 'instagram') return 'Больше лайков'
      if (platform === 'telegram') return 'Больше реакций'
      if (platform === 'reddit') return 'Больше очков'
      return 'Больше реакций и лайков'
    case 'comments':
      return 'Больше комментариев'
    case 'forwards':
      return 'Больше пересылок'
    case 'added':
      return 'Сначала добавленные'
    case 'share':
      return 'Больше доля от подписчиков'
  }
}

// Последняя открытая вкладка: «Залёты» без format в адресе открываются на ней.
// Вид «Добавленные» не запоминается: сводка и бот ведут в «Авто».
const TAB_KEY = 'cf2_feed_format'

export function rememberedFormat(): FeedFormat {
  try {
    return localStorage.getItem(TAB_KEY) === 'text' ? 'text' : 'video'
  } catch {
    return 'video'
  }
}

export function rememberFormat(format: FeedFormat): void {
  try {
    localStorage.setItem(TAB_KEY, format)
  } catch {
    // хранилище недоступно — вкладка не запомнится
  }
}

// Фильтры вкладки: сортировка, площадка, канал. В адресе — фильтры открытой
// вкладки; фильтры соседней ждут в sessionStorage, пока к ней не вернутся.
// Подборка, период и «только ×2+» общие и остаются в адресе.
// У «Добавленных» свои сортировка, канал и период (отдельный слот video:added),
// а фильтры «Авто» — свои и общие — ждут в слоте video.
export const TAB_PARAMS = ['s', 'p', 'src', 'lang'] as const
const SHARED_PARAMS = ['c', 'd', 'all'] as const
const AUTO_PARAMS = [...TAB_PARAMS, ...SHARED_PARAMS]
const ADDED_PARAMS = ['s', 'src', 'd', 'lang'] as const

type Slot = FeedFormat | 'video:added'
const stashKey = (slot: Slot) => `cf2_feed_tab_${slot}`

function stash(slot: Slot, params: URLSearchParams, keys: readonly string[]): void {
  const kept = new URLSearchParams()
  for (const k of keys) {
    const v = params.get(k)
    if (v) kept.set(k, v)
  }
  try {
    sessionStorage.setItem(stashKey(slot), kept.toString())
  } catch {
    // без хранилища вкладка откроется с фильтрами по умолчанию
  }
}

/** Только `keys`: общие фильтры из слота video не перебивают текущие при смене вкладки. */
function restore(slot: Slot, keys: readonly string[]): URLSearchParams {
  const out = new URLSearchParams()
  try {
    const kept = new URLSearchParams(sessionStorage.getItem(stashKey(slot)) ?? '')
    for (const k of keys) {
      const v = kept.get(k)
      if (v) out.set(k, v)
    }
  } catch {
    // без хранилища — фильтры по умолчанию
  }
  return out
}

export function stashTabParams(format: FeedFormat, params: URLSearchParams): void {
  stash(format, params, TAB_PARAMS)
}

export function restoreTabParams(format: FeedFormat): URLSearchParams {
  return restore(format, TAB_PARAMS)
}

/** «Авто» → «Добавленные»: фильтры «Авто» в слот, в адресе — фильтры «Добавленных». */
export function enterAdded(prev: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(prev)
  stash('video', out, AUTO_PARAMS)
  for (const k of AUTO_PARAMS) out.delete(k)
  restore('video:added', ADDED_PARAMS).forEach((v, k) => out.set(k, v))
  out.set('format', 'video')
  out.set('view', 'added')
  return out
}

export function leaveAdded(prev: URLSearchParams): URLSearchParams {
  const out = new URLSearchParams(prev)
  stash('video:added', out, ADDED_PARAMS)
  for (const k of ADDED_PARAMS) out.delete(k)
  out.delete('view')
  restore('video', AUTO_PARAMS).forEach((v, k) => out.set(k, v))
  return out
}

/** Общие фильтры «Авто», пока открыты «Добавленные»: по ним считаются вкладки. */
export function stashedAutoShared(): URLSearchParams {
  return restore('video', SHARED_PARAMS)
}
